import { pipe, Either, JSON, Runtime } from "fp";

import * as Moment from "moment-timezone";
import React from "react";

import { Order } from "~/Order";
import { Search } from "~/Search";
import { Time, Time as GenericTime } from "~/Time";
import * as Typography from "~/Typography";

export type DateTime = { readonly dateTime: number };

export function DateTime({
  Time = DateTime.Default.Time,
  Separator = DateTime.Default.Separator,
  Date = DateTime.Default.Date,
  ...props
}: DateTime.Props) {
  return React.useMemo(
    () => (
      <>
        <Time {...props} />
        <Separator />
        <Date {...props} />
      </>
    ),
    [props.dateTime]
  );
}

export namespace DateTime {
  export type Props = DateTime & {
    readonly Time?: typeof Default.Time;
    readonly Separator?: typeof Default.Separator;
    readonly Date?: typeof Default.Date;
  };

  export const is = (dateTime?: unknown): dateTime is DateTime =>
    typeof dateTime === "number" &&
    !isNaN(dateTime) &&
    Moment.default(dateTime).isValid();

  export const now = (): DateTime => ({ dateTime: Date.now() });

  export const isBefore = ({ dateTime: beforeTime }: DateTime) => ({
    dateTime: afterTime,
  }: DateTime): boolean => beforeTime < afterTime;

  export const isAfter = ({ dateTime: afterTime }: DateTime) => ({
    dateTime: beforeTime,
  }: DateTime): boolean => afterTime > beforeTime;

  export const isStartOfDay = ({ dateTime }: DateTime): boolean =>
    Moment.default(dateTime).toISOString() ===
    Moment.default(dateTime).startOf("day").toISOString();

  export const isEndOfDay = ({ dateTime }: DateTime): boolean =>
    Moment.default(dateTime).toISOString() ===
    Moment.default(dateTime).endOf("day").toISOString();

  export const isStartOfDayForZone = (
    { dateTime }: DateTime,
    { zone }: Time.Zone
  ): boolean =>
    Moment.default(dateTime).tz(zone).format() ===
    Moment.default(dateTime).tz(zone).startOf("day").format();

  export const isEndOfDayForZone = (
    { dateTime }: DateTime,
    { zone }: Time.Zone
  ): boolean =>
    Moment.default(dateTime).tz(zone).format() ===
    Moment.default(dateTime).tz(zone).endOf("day").format();

  export const isBeforeCurrentDay = (
    datetime: DateTime,
    zone?: Time.Zone
  ): boolean =>
    zone
      ? _isBeforeCurrentDayForZone(datetime, zone)
      : _isBeforeCurrentDay(datetime);

  export const _isBeforeCurrentDay = ({ dateTime }: DateTime): boolean =>
    Moment.default(dateTime).endOf("day").isBefore(Moment.default(), "day");

  export const _isBeforeCurrentDayForZone = (
    { dateTime }: DateTime,
    { zone }: Time.Zone
  ): boolean =>
    Moment.default(dateTime).tz(zone, true).isBefore(Moment.default(), "day");

  export const { equals, compare } = Order.fromCompare<DateTime>((a, b) =>
    Order.number().compare(a.dateTime, b.dateTime)
  );

  export const toSearch: Search.From<DateTime> = (dateTime) =>
    Search.join(
      Search.string(toHMM(dateTime)),
      Search.string(toHMMSS(dateTime)),
      Search.string(to24HourHHMMSS(dateTime)),
      Search.string(toDateOnly(dateTime)),
      Search.string(toDateOnlyMDYY(dateTime)),
      Search.string(toDateOnlyMMMDYYYY(dateTime)),
      Search.string(toISO(dateTime)),
      Search.string(toISOWithoutTime(dateTime)),
      Search.string(toISOLocalWithoutTime(dateTime))
    );

  export const fromNumber = (number: number): DateTime => ({
    dateTime: number,
  });

  export const fromISOUnsafe = (str: string): DateTime => ({
    dateTime: Moment.default(str).valueOf(),
  });

  export const fromString = (string: string): Either.ErrorOr<DateTime> =>
    pipe(momentFromString(string), Either.map(fromMoment));

  export const fromStringWithFormat = (string: string) => (
    format: string
  ): Either.ErrorOr<DateTime> =>
    pipe(momentFromString(string, format), Either.map(fromMoment));

  export const from24HourHHMMSS = (
    string: string,
    { dateTime }: DateTime
  ): Either.ErrorOr<DateTime> =>
    pipe(
      momentFromString(string, "HH:mm:ss"),
      Either.map((momentWithoutDateTime) => {
        const momentFromDateTime = Moment.default(dateTime);
        const momentAtDateTime = momentWithoutDateTime
          .dayOfYear(momentFromDateTime.dayOfYear())
          .year(momentFromDateTime.year());

        return fromMoment(momentAtDateTime);
      })
    );

  export const fromHHMMSSa = (
    string: string,
    { dateTime }: DateTime
  ): Either.ErrorOr<DateTime> =>
    pipe(
      momentFromString(string, "hh:mm:ss a"),
      Either.map((momentWithoutDateTime) => {
        const momentFromDateTime = Moment.default(dateTime);
        const momentAtDateTime = momentWithoutDateTime
          .dayOfYear(momentFromDateTime.dayOfYear())
          .year(momentFromDateTime.year());

        return fromMoment(momentAtDateTime);
      })
    );

  export type TimeUnits = "hours" | "days";

  export const add = ({ dateTime }: DateTime) => (
    amount: number,
    unit: TimeUnits
  ): DateTime =>
    fromNumber(Moment.default(dateTime).add(amount, unit).valueOf());

  export const subtract = ({ dateTime }: DateTime) => (
    amount: number,
    unit: TimeUnits
  ): DateTime =>
    fromNumber(Moment.default(dateTime).subtract(amount, unit).valueOf());

  export const toMonth = ({ dateTime }: DateTime): number =>
    Moment.default(dateTime).month();

  export const toYear = ({ dateTime }: DateTime): number =>
    Moment.default(dateTime).year();

  export const toDayInterval = (
    { dateTime }: DateTime,
    time: Time.Zone = { zone: Moment.tz.guess() }
  ): Time.Interval.Stopped => ({
    interval: {
      start: fromMoment(
        Moment.default(dateTime).startOf("day").tz(time.zone, true)
      ),
      stop: fromMoment(
        Moment.default(dateTime).endOf("day").tz(time.zone, true)
      ),
    },
  });

  export const offsetByTimezone = (
    interval: Time.Interval.Stopped,
    zone: Time.Zone
  ) => {
    return {
      interval: {
        start: fromMoment(
          Moment.default(interval.interval.start.dateTime).tz(zone.zone, true)
        ),
        stop: fromMoment(
          Moment.default(interval.interval.stop.dateTime).tz(zone.zone, true)
        ),
      },
    };
  };

  export const toStartOfYear = ({ dateTime }: DateTime = now()): DateTime =>
    fromMoment(Moment.default(dateTime).startOf("year"));

  export const toStartOfMonth = ({ dateTime }: DateTime = now()): DateTime =>
    fromMoment(Moment.default(dateTime).startOf("month"));

  export const toStartOfDay = ({ dateTime }: DateTime = now()): DateTime =>
    fromMoment(Moment.default(dateTime).startOf("day"));

  export const toStartOfHour = ({ dateTime }: DateTime = now()): DateTime =>
    fromMoment(Moment.default(dateTime).startOf("hour"));

  export const toStartOfMinute = ({ dateTime }: DateTime = now()): DateTime =>
    fromMoment(Moment.default(dateTime).startOf("minute"));

  export const toStartOfDayForZone = (
    { dateTime }: DateTime = now(),
    time: Time.Zone = { zone: Moment.tz.guess() }
  ): DateTime =>
    fromMoment(Moment.default(dateTime).tz(time.zone).startOf("day"));

  export const toEndOfDayForZone = (
    { dateTime }: DateTime = now(),
    time: Time.Zone = { zone: Moment.tz.guess() }
  ): DateTime =>
    fromMoment(Moment.default(dateTime).tz(time.zone).endOf("day"));

  export const toEndOfDay = ({ dateTime }: DateTime = now()): DateTime =>
    fromMoment(Moment.default(dateTime).endOf("day"));

  export const toEndOfHour = ({ dateTime }: DateTime = now()): DateTime =>
    fromMoment(Moment.default(dateTime).endOf("hour"));

  export const toValue = ({ dateTime }: DateTime): number => dateTime;

  export const toMoment = ({ dateTime }: DateTime): Moment.Moment =>
    Moment.default(dateTime);

  type Formatted = string;
  type Formatter = (dateTime: DateTime, time?: Time.Zone) => Formatted;
  type Formatable = (
    dateTime: DateTime,
    time?: Time.Zone
  ) => (format: string) => Formatted;

  export const toList: Formatter = (
    { dateTime }: DateTime,
    time: Time.Zone = { zone: Moment.tz.guess() }
  ) => Moment.default(dateTime).tz(time.zone).format("h,mm,A,a");

  export const toHa: Formatter = (
    { dateTime },
    time: Time.Zone = { zone: Moment.tz.guess() }
  ) => {
    const formattedTime = Moment.default(dateTime).tz(time.zone).format("ha");
    return formattedTime.substr(0, formattedTime.length - 1);
  };

  export const toHA: Formatter = (
    { dateTime },
    time: Time.Zone = { zone: Moment.tz.guess() }
  ) => Moment.default(dateTime).tz(time.zone).format("h A");

  export const toHMM: Formatter = (
    { dateTime },
    time: Time.Zone = { zone: Moment.tz.guess() }
  ) => Moment.default(dateTime).tz(time.zone).format("h:mm A");

  export const toHHMM: Formatter = (
    { dateTime },
    time: Time.Zone = { zone: Moment.tz.guess() }
  ) => Moment.default(dateTime).tz(time.zone).format("hh:mm A");

  export const toHMMSS: Formatter = (
    { dateTime },
    time: Time.Zone = { zone: Moment.tz.guess() }
  ) => Moment.default(dateTime).tz(time.zone).format("h:mm:ss A");

  export const to24HourHHMMSS: Formatter = (
    { dateTime },
    time: Time.Zone = { zone: Moment.tz.guess() }
  ) => Moment.default(dateTime).tz(time.zone).format("HH:mm:ss");

  export const toFormat: Formatable = (
    { dateTime },
    time: Time.Zone = { zone: Moment.tz.guess() }
  ) => (format: string) =>
    Moment.default(dateTime).tz(time.zone).format(format);

  export const toDateOnly: Formatter = ({ dateTime }) =>
    Moment.default(dateTime).format("MM/DD/YYYY");

  export const toDateOnlyMDYY: Formatter = ({ dateTime }) =>
    Moment.default(dateTime).format("M/D/YY");

  export const toDateOnlyMMMDYYYY: Formatter = ({
    dateTime,
  }: DateTime): Formatted => Moment.default(dateTime).format("MMM D, YYYY");

  export const toDateOnlyMMMDYYYYIgnoreTimezone: Formatter = ({
    dateTime,
  }: DateTime): Formatted =>
    Moment.default(dateTime).utc().format("MMM D, YYYY");

  export const toDayNameOnly: Formatter = (
    { dateTime },
    time: Time.Zone = { zone: Moment.tz.guess() }
  ) => Moment.default(dateTime).tz(time.zone).format("ddd");

  export const toDayName: Formatter = (
    { dateTime },
    time: Time.Zone = { zone: Moment.tz.guess() }
  ) => Moment.default(dateTime).tz(time.zone).format("ddd DD");

  export const toISO: Formatter = ({ dateTime }: DateTime) =>
    Moment.default(dateTime).toISOString();

  export const toISOLocal: Formatter = ({ dateTime }: DateTime) =>
    Moment.default(dateTime).local().toISOString();

  export const toISOWithoutTime: Formatter = (dateTime) =>
    toISO(dateTime).split("T")[0];

  export const toISOLocalWithoutTime: Formatter = ({ dateTime }) =>
    Moment.default(dateTime).local().format("YYYY-MM-DD");

  export const toLocal: Formatter = ({ dateTime }) =>
    Moment.default(dateTime).local().format("YYYY-MM-DD HH:mm:ss");

  const fromMoment = (moment: Moment.Moment): DateTime => ({
    dateTime: moment.valueOf(),
  });

  const momentFromString = (
    string: string,
    format: Moment.MomentFormatSpecification = Moment.defaultFormat
  ): Either.ErrorOr<Moment.Moment> =>
    pipe(
      Moment.default(string, format),
      Either.fromPredicate(
        (moment) => moment.isValid(),
        () => onParseError(string)
      )
    );

  const onParseError = (input: string): Error =>
    Error(
      `Couldn't convert \`${JSON.Stringify.Always.pretty(
        input
      )}\` into a \`DateTime\``
    );

  export const spy = <A extends Time.DateTime>(value: A, message = `date:`) => {
    console.log(`${message} date=${Time.DateTime.toLocal(value)}`);
    return value;
  };

  export namespace Default {
    export const Time: React.Fn<DateTime> = (dateTime) => (
      <Typography.Component strong>
        {toHMMSS(dateTime, GenericTime.Zone.use())}
      </Typography.Component>
    );

    export const Separator: React.Fn = () => (
      <Typography.Component>&nbsp;on&nbsp;</Typography.Component>
    );

    export const Date: React.Fn<DateTime> = (dateTime) => (
      <>{toDateOnly(dateTime)}</>
    );
  }

  export namespace Response {
    export const runtime = () =>
      new Runtime.Type(
        "DateTime",
        is,
        (input, context) =>
          pipe(
            fromString(`${input}`),
            Either.map(Runtime.success),
            Either.getOrElse(() => Runtime.failure(input, context))
          ),
        Runtime.identity
      );
  }
}
